import { useQuery } from "@tanstack/react-query";
import {
  AlertCircle,
  ArrowDownRight,
  ArrowUpRight,
  ChevronDown,
  ChevronUp,
  History,
  Minus,
  RefreshCw,
  Search,
  Sparkles,
  Users,
} from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { EmployeeDetailDrawer } from "../components/EmployeeDetailDrawer.js";
import { TrendBadge } from "../components/TrendBadge.js";
import { AppLayout } from "../components/layout/AppLayout.js";
import { Header } from "../components/layout/Header.js";
import { RiskBadge } from "../components/ui/RiskBadge.js";
import { ScoreBar } from "../components/ui/ScoreBar.js";
import { type ExitPatternAnalysis, type RiskLevel, aiApi, employeesApi } from "../lib/api.js";

type Filter = "all" | RiskLevel;

// ── Exit Pattern Panel ────────────────────────────────────────────────────────

function LiftArrow({ lift }: { lift: number }) {
  if (lift >= 1.3) return <ArrowUpRight size={12} style={{ color: "var(--risk-high)" }} />;
  if (lift <= 0.7) return <ArrowDownRight size={12} style={{ color: "var(--risk-low)" }} />;
  return <Minus size={12} style={{ color: "var(--text-muted)" }} />;
}

function SeverityDot({ severity }: { severity: "critical" | "warning" | "info" }) {
  const color =
    severity === "critical"
      ? "var(--risk-high)"
      : severity === "warning"
        ? "var(--accent)"
        : "var(--accent-gold)";
  return (
    <span
      style={{
        display: "inline-block",
        width: 7,
        height: 7,
        borderRadius: "50%",
        background: color,
        flexShrink: 0,
        marginTop: 2,
      }}
    />
  );
}

function ExitPatternPanel({ data }: { data: ExitPatternAnalysis }) {
  const [showSignals, setShowSignals] = useState(false);
  const [showDepts, setShowDepts] = useState(false);

  const topLiftSignals = [...data.signalLifts]
    .filter((s) => s.leaverCount > 0)
    .sort((a, b) => b.lift - a.lift)
    .slice(0, 6);

  const dataQualityLabel = {
    sufficient: "Ausreichend",
    limited: "Eingeschränkt",
    insufficient: "Zu wenig Daten",
  }[data.insights.dataQuality];

  const dataQualityColor = {
    sufficient: "var(--risk-low-text)",
    limited: "var(--accent)",
    insufficient: "var(--text-muted)",
  }[data.insights.dataQuality];

  return (
    <div className="card mb-4">
      {/* Header */}
      <div
        className="flex items-center gap-3 px-4 py-3"
        style={{ borderBottom: "1px solid var(--border)" }}
      >
        <History size={14} style={{ color: "var(--accent)" }} />
        <span className="text-sm font-semibold" style={{ color: "var(--text-primary)" }}>
          Historische Abgangs-Analyse
        </span>
        <span
          className="text-xs px-2 py-0.5 rounded font-mono"
          style={{ background: "var(--bg-hover)", color: "var(--text-muted)" }}
        >
          {data.totalExits} Abgänge analysiert · Ø {data.avgTenureAtExitMonths}M
          Betriebszugehörigkeit
        </span>
        <span
          className="text-xs px-2 py-0.5 rounded ml-auto"
          style={{ color: dataQualityColor, border: `1px solid ${dataQualityColor}`, opacity: 0.9 }}
        >
          Datenqualität: {dataQualityLabel}
        </span>
        {data.insights.weightAdjustments.length > 0 && (
          <span
            className="flex items-center gap-1 text-xs px-2 py-0.5 rounded"
            style={{
              background: "var(--accent-dim)",
              color: "var(--accent)",
              border: "1px solid rgba(234,91,11,0.3)",
            }}
          >
            <Sparkles size={10} />
            {data.insights.weightAdjustments.length} Gewichte kalibriert
          </span>
        )}
      </div>

      <div className="p-4 grid gap-4" style={{ gridTemplateColumns: "1fr 1fr" }}>
        {/* Left: AI Insights */}
        <div>
          <p className="text-xs font-medium mb-2" style={{ color: "var(--text-muted)" }}>
            KI-ERKENNTNISSE
          </p>
          {data.insights.insights.length === 0 ? (
            <p className="text-xs" style={{ color: "var(--text-muted)" }}>
              {data.insights.summary}
            </p>
          ) : (
            <div className="flex flex-col gap-2">
              <p
                className="text-xs mb-1"
                style={{ color: "var(--text-secondary)", lineHeight: 1.5 }}
              >
                {data.insights.summary}
              </p>
              {data.insights.insights.map((insight) => (
                <div
                  key={insight.id}
                  className="rounded p-3"
                  style={{ background: "var(--bg-surface)", border: "1px solid var(--border)" }}
                >
                  <div className="flex items-start gap-2 mb-1">
                    <SeverityDot severity={insight.severity} />
                    <span className="text-xs font-medium" style={{ color: "var(--text-primary)" }}>
                      {insight.title}
                    </span>
                  </div>
                  <p
                    className="text-[11px] mb-1.5"
                    style={{ color: "var(--text-secondary)", lineHeight: 1.5 }}
                  >
                    {insight.description}
                  </p>
                  <div
                    className="text-[10px] px-2 py-1 rounded"
                    style={{
                      background: "var(--accent-dim)",
                      color: "var(--accent)",
                      borderLeft: "2px solid var(--accent)",
                    }}
                  >
                    → {insight.recommendation}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Right: Statistical Summary */}
        <div className="flex flex-col gap-3">
          {/* Tenure Distribution */}
          <div>
            <p className="text-xs font-medium mb-2" style={{ color: "var(--text-muted)" }}>
              TENURE BEIM AUSSCHEIDEN
            </p>
            <div className="flex flex-col gap-1">
              {data.tenureDistribution.map((t) => (
                <div key={t.bucket} className="flex items-center gap-2">
                  <span
                    className="text-[11px] w-24 shrink-0"
                    style={{ color: "var(--text-secondary)" }}
                  >
                    {t.bucket}
                  </span>
                  <div
                    className="flex-1 h-1.5 rounded-full"
                    style={{ background: "var(--bg-hover)" }}
                  >
                    <div
                      className="h-full rounded-full"
                      style={{
                        width: `${t.pct}%`,
                        background:
                          t.pct >= 35
                            ? "var(--risk-high)"
                            : t.pct >= 20
                              ? "var(--accent)"
                              : "var(--accent-gold)",
                      }}
                    />
                  </div>
                  <span
                    className="text-[11px] font-mono w-14 text-right shrink-0"
                    style={{ color: "var(--text-muted)" }}
                  >
                    {t.count} ({t.pct}%)
                  </span>
                </div>
              ))}
            </div>
          </div>

          {/* Signal Lifts — collapsible */}
          <div>
            <button
              type="button"
              className="flex items-center gap-1 text-xs font-medium mb-2 w-full"
              style={{
                color: "var(--text-muted)",
                background: "transparent",
                border: "none",
                cursor: "pointer",
                textAlign: "left",
              }}
              onClick={() => setShowSignals(!showSignals)}
            >
              PRÄDIKTIVE SIGNALE (LIFT)
              {showSignals ? <ChevronUp size={11} /> : <ChevronDown size={11} />}
            </button>
            {showSignals && (
              <div className="flex flex-col gap-1">
                {topLiftSignals.map((s) => (
                  <div key={s.signalKey} className="flex items-center gap-2">
                    <LiftArrow lift={s.lift} />
                    <span
                      className="text-[11px] flex-1 truncate"
                      style={{ color: "var(--text-secondary)" }}
                    >
                      {s.signalLabel}
                    </span>
                    <span
                      className="text-[10px] font-mono px-1.5 py-0.5 rounded shrink-0"
                      style={{
                        background:
                          s.lift >= 1.5
                            ? "var(--risk-high-bg)"
                            : s.lift <= 0.7
                              ? "var(--risk-low-bg)"
                              : "var(--bg-hover)",
                        color:
                          s.lift >= 1.5
                            ? "var(--risk-high-text)"
                            : s.lift <= 0.7
                              ? "var(--risk-low-text)"
                              : "var(--text-muted)",
                      }}
                    >
                      ×{s.lift.toFixed(2)}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Dept exit rates — collapsible */}
          {data.exitByDepartment.length > 0 && (
            <div>
              <button
                type="button"
                className="flex items-center gap-1 text-xs font-medium mb-2 w-full"
                style={{
                  color: "var(--text-muted)",
                  background: "transparent",
                  border: "none",
                  cursor: "pointer",
                  textAlign: "left",
                }}
                onClick={() => setShowDepts(!showDepts)}
              >
                <Users size={10} />
                ABGANGSRATEN NACH ABTEILUNG
                {showDepts ? <ChevronUp size={11} /> : <ChevronDown size={11} />}
              </button>
              {showDepts && (
                <div className="flex flex-col gap-1">
                  {data.exitByDepartment.slice(0, 5).map((d) => (
                    <div key={d.department} className="flex items-center gap-2">
                      <span
                        className="text-[11px] flex-1 truncate"
                        style={{ color: "var(--text-secondary)" }}
                      >
                        {d.department}
                      </span>
                      <span
                        className="text-[10px] font-mono"
                        style={{ color: "var(--text-muted)" }}
                      >
                        {d.exits} Abgänge
                      </span>
                      <span
                        className="text-[10px] font-mono px-1.5 py-0.5 rounded shrink-0"
                        style={{
                          background:
                            d.exitRate >= 30
                              ? "var(--risk-high-bg)"
                              : d.exitRate >= 15
                                ? "var(--accent-dim)"
                                : "var(--bg-hover)",
                          color:
                            d.exitRate >= 30
                              ? "var(--risk-high-text)"
                              : d.exitRate >= 15
                                ? "var(--accent)"
                                : "var(--text-muted)",
                        }}
                      >
                        {d.exitRate}%
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────────────

export function RetentionRadar() {
  const { t } = useTranslation();
  const [filter, setFilter] = useState<Filter>("all");
  const [search, setSearch] = useState("");
  const [showExitPanel, setShowExitPanel] = useState(false);
  const [selectedEmployeeId, setSelectedEmployeeId] = useState<number | null>(null);
  const [departmentFilter, setDepartmentFilter] = useState("");
  const [fkFilter, setFkFilter] = useState("");
  const [hideConfirmedLeaving, setHideConfirmedLeaving] = useState(true);
  // Spalten-Sortierung: null = Server-Default (Risiko-Score absteigend)
  const [sortKey, setSortKey] = useState<"tenure" | "score" | "trend" | null>(null);
  const [sortDir, setSortDir] = useState<"asc" | "desc">("desc");

  // Klick-Zyklus pro Spalte: absteigend → aufsteigend → Default
  function toggleSort(key: "tenure" | "score" | "trend") {
    if (sortKey !== key) {
      setSortKey(key);
      setSortDir("desc");
      return;
    }
    if (sortDir === "desc") {
      setSortDir("asc");
      return;
    }
    setSortKey(null);
    setSortDir("desc");
  }

  const { data, isLoading, isError, error, refetch, isFetching } = useQuery({
    queryKey: ["employees"],
    queryFn: employeesApi.getAll,
    staleTime: 5 * 60_000,
    retry: 1,
  });

  const {
    data: exitData,
    isLoading: exitLoading,
    isError: exitError,
    refetch: refetchExit,
    isFetching: exitFetching,
  } = useQuery({
    queryKey: ["exit-patterns"],
    queryFn: aiApi.getExitPatterns,
    staleTime: 30 * 60_000, // 30 min — LLM call, expensive
    retry: 0,
    enabled: showExitPanel, // lazy: nur laden wenn Panel geöffnet
  });

  const employees = data?.employees ?? [];
  const summary = data?.summary;

  const departments = Array.from(
    new Set(employees.map((e) => e.department).filter((d): d is string => d !== null && d !== "")),
  ).sort();

  // W16: Filter nach Führungskraft (Feld kommt rollenabhängig — nur hrbp/admin/manager)
  const fuehrungskraefte = Array.from(
    new Set(
      employees.map((e) => e.fuehrungskraft).filter((f): f is string => f != null && f !== ""),
    ),
  ).sort();

  const filtered = employees.filter((e) => {
    const matchFilter = filter === "all" || e.riskLevel === filter;
    const matchSearch =
      search === "" ||
      e.displayName.toLowerCase().includes(search.toLowerCase()) ||
      (e.department ?? "").toLowerCase().includes(search.toLowerCase()) ||
      (e.position ?? "").toLowerCase().includes(search.toLowerCase()) ||
      (e.team ?? "").toLowerCase().includes(search.toLowerCase());
    const matchDept = departmentFilter === "" || e.department === departmentFilter;
    const matchFk = fkFilter === "" || e.fuehrungskraft === fkFilter;
    const matchLeaving = !(hideConfirmedLeaving && e.isConfirmedLeaving);
    return matchFilter && matchSearch && matchDept && matchFk && matchLeaving;
  });

  // Sortierung anwenden (null-Werte immer ans Ende)
  if (sortKey !== null) {
    const dir = sortDir === "asc" ? 1 : -1;
    filtered.sort((a, b) => {
      const av =
        sortKey === "tenure"
          ? a.tenureMonths
          : sortKey === "score"
            ? a.retentionScore
            : (a.trendPp ?? null);
      const bv =
        sortKey === "tenure"
          ? b.tenureMonths
          : sortKey === "score"
            ? b.retentionScore
            : (b.trendPp ?? null);
      if (av == null && bv == null) return 0;
      if (av == null) return 1;
      if (bv == null) return -1;
      return (av - bv) * dir;
    });
  }

  /** Pfeil-Indikator für sortierbare Spaltenköpfe */
  function sortArrow(key: "tenure" | "score" | "trend"): string {
    if (sortKey !== key) return "";
    return sortDir === "asc" ? " ▲" : " ▼";
  }

  const FILTERS: { key: Filter; label: string; color?: string; count?: number }[] = [
    { key: "all", label: t("retention.filterAll"), count: employees.length },
    {
      key: "high",
      label: t("retention.filterHigh"),
      color: "var(--risk-high-text)",
      count: summary?.high,
    },
    {
      key: "medium",
      label: t("retention.filterMedium"),
      color: "var(--risk-medium-text)",
      count: summary?.medium,
    },
    {
      key: "low",
      label: t("retention.filterLow"),
      color: "var(--risk-low-text)",
      count: summary?.low,
    },
  ];

  return (
    <AppLayout>
      <Header title={t("retention.title")} subtitle={t("retention.subtitle")} />
      <div className="flex-1 overflow-hidden flex flex-col px-6 py-5 page-enter">
        {/* Toolbar */}
        <div className="flex items-center gap-3 mb-5">
          {/* Search */}
          <div
            className="flex items-center gap-2 px-3 py-2 rounded flex-1 max-w-xs"
            style={{ background: "var(--bg-card)", border: "1px solid var(--border)" }}
          >
            <Search size={13} style={{ color: "var(--text-muted)" }} />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={t("common.search")}
              className="bg-transparent text-sm outline-none w-full"
              style={{ color: "var(--text-primary)" }}
            />
          </div>

          {/* Filter pills */}
          <div className="flex gap-1.5">
            {FILTERS.map(({ key, label, color, count }) => (
              <button
                type="button"
                key={key}
                onClick={() => setFilter(key)}
                className="px-3 py-1.5 rounded text-xs font-medium transition-colors flex items-center gap-1.5"
                style={{
                  background:
                    filter === key
                      ? color
                        ? `color-mix(in srgb, ${color} 9%, transparent)`
                        : "var(--bg-hover)"
                      : "transparent",
                  color: filter === key ? (color ?? "var(--text-primary)") : "var(--text-muted)",
                  border: `1px solid ${filter === key ? (color ?? "var(--border-light)") : "var(--border)"}`,
                }}
              >
                {label}
                {count !== undefined && (
                  <span className="font-mono-data" style={{ opacity: 0.7, fontSize: "10px" }}>
                    {count}
                  </span>
                )}
              </button>
            ))}
          </div>

          {/* Department dropdown */}
          <select
            value={departmentFilter}
            onChange={(e) => setDepartmentFilter(e.target.value)}
            className="px-3 py-1.5 rounded text-xs font-medium transition-colors"
            style={{
              background: departmentFilter !== "" ? "var(--bg-hover)" : "transparent",
              color: departmentFilter !== "" ? "var(--text-primary)" : "var(--text-muted)",
              border: `1px solid ${departmentFilter !== "" ? "var(--border-light)" : "var(--border)"}`,
              outline: "none",
              cursor: "pointer",
              appearance: "none",
              paddingRight: "1.5rem",
            }}
          >
            <option value="">Alle Abteilungen</option>
            {departments.map((d) => (
              <option key={d} value={d}>
                {d}
              </option>
            ))}
          </select>

          {/* W16: FK dropdown (nur sichtbar, wenn die Rolle FK-Namen bekommt) */}
          {fuehrungskraefte.length > 0 && (
            <select
              value={fkFilter}
              onChange={(e) => setFkFilter(e.target.value)}
              className="px-3 py-1.5 rounded text-xs font-medium transition-colors"
              style={{
                background: fkFilter !== "" ? "var(--bg-hover)" : "transparent",
                color: fkFilter !== "" ? "var(--text-primary)" : "var(--text-muted)",
                border: `1px solid ${fkFilter !== "" ? "var(--border-light)" : "var(--border)"}`,
                outline: "none",
                cursor: "pointer",
                appearance: "none",
                paddingRight: "1.5rem",
              }}
            >
              <option value="">Alle Führungskräfte</option>
              {fuehrungskraefte.map((f) => (
                <option key={f} value={f}>
                  {f}
                </option>
              ))}
            </select>
          )}

          {/* Hide confirmed leaving toggle */}
          <button
            type="button"
            onClick={() => setHideConfirmedLeaving(!hideConfirmedLeaving)}
            className="px-3 py-1.5 rounded text-xs font-medium transition-colors"
            style={{
              background: hideConfirmedLeaving ? "var(--accent-dim)" : "transparent",
              color: hideConfirmedLeaving ? "var(--accent)" : "var(--text-muted)",
              border: `1px solid ${hideConfirmedLeaving ? "var(--accent)" : "var(--border)"}`,
            }}
          >
            {hideConfirmedLeaving ? "Abgänge einblenden" : "Abgänge ausblenden"}
          </button>

          {/* Exit Analysis toggle */}
          <button
            type="button"
            onClick={() => setShowExitPanel(!showExitPanel)}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded text-xs transition-colors"
            style={{
              border: `1px solid ${showExitPanel ? "var(--accent)" : "var(--border)"}`,
              color: showExitPanel ? "var(--accent)" : "var(--text-muted)",
              background: showExitPanel ? "var(--accent-dim)" : "transparent",
            }}
          >
            <History size={11} />
            Historische Analyse
          </button>

          {/* Refresh */}
          <button
            type="button"
            onClick={() => {
              void refetch();
              if (showExitPanel) void refetchExit();
            }}
            disabled={isFetching}
            className="ml-auto flex items-center gap-1.5 px-3 py-1.5 rounded text-xs transition-colors"
            style={{
              border: "1px solid var(--border)",
              color: "var(--text-muted)",
              background: "transparent",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.borderColor = "var(--border-light)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.borderColor = "var(--border)";
            }}
          >
            <RefreshCw size={11} className={isFetching ? "animate-spin" : ""} />
            {isFetching ? "Lädt…" : "Aktualisieren"}
          </button>
        </div>

        {/* Exit Pattern Panel */}
        {showExitPanel && (
          <div>
            {exitLoading || exitFetching ? (
              <div className="card mb-4 flex items-center justify-center gap-3 py-8">
                <RefreshCw size={14} className="animate-spin" style={{ color: "var(--accent)" }} />
                <span className="text-sm" style={{ color: "var(--text-muted)" }}>
                  Historische Abgänge werden analysiert…
                </span>
              </div>
            ) : exitError ? (
              <div className="card mb-4 flex items-center gap-2 px-4 py-3">
                <AlertCircle size={14} style={{ color: "var(--risk-high)" }} />
                <span className="text-xs" style={{ color: "var(--text-secondary)" }}>
                  Analyse nicht verfügbar. Nur HRBP/Admin-Rolle hat Zugriff.
                </span>
              </div>
            ) : exitData ? (
              <ExitPatternPanel data={exitData} />
            ) : null}
          </div>
        )}

        {/* Table */}
        <div className="card flex-1 overflow-hidden flex flex-col">
          {/* Header row */}
          <div
            className="grid text-xs font-medium px-4 py-3"
            style={{
              gridTemplateColumns: "180px 1fr 105px 170px 90px 1fr 80px",
              color: "var(--text-muted)",
              borderBottom: "1px solid var(--border)",
            }}
          >
            <span>
              {t("retention.colId")} <span style={{ opacity: 0.4, fontWeight: 400 }}>↗ Detail</span>
            </span>
            <span>{t("retention.colDept")}</span>
            <span
              className="cursor-pointer select-none"
              style={{ color: sortKey === "tenure" ? "var(--accent)" : undefined }}
              title="Nach Betriebszugehörigkeit sortieren"
              onClick={() => toggleSort("tenure")}
            >
              Zugehörigkeit{sortArrow("tenure")}
            </span>
            <span
              className="cursor-pointer select-none"
              style={{ color: sortKey === "score" ? "var(--accent)" : undefined }}
              title="Nach Risiko-Score sortieren"
              onClick={() => toggleSort("score")}
            >
              {t("retention.colScore")}
              {sortArrow("score")}
            </span>
            <span
              className="cursor-pointer select-none"
              style={{ color: sortKey === "trend" ? "var(--accent)" : undefined }}
              title="Nach Trend sortieren"
              onClick={() => toggleSort("trend")}
            >
              Trend{sortArrow("trend")}
            </span>
            <span>Signale</span>
            <span>Risiko</span>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto">
            {isLoading && (
              <div className="flex flex-col items-center justify-center h-40 gap-3">
                <RefreshCw size={18} className="animate-spin" style={{ color: "var(--accent)" }} />
                <p className="text-xs" style={{ color: "var(--text-muted)" }}>
                  Personio-Daten werden geladen…
                </p>
              </div>
            )}

            {isError && (
              <div className="flex items-center justify-center h-32 gap-2 px-6">
                <AlertCircle size={15} style={{ color: "var(--risk-high)" }} />
                <p className="text-sm" style={{ color: "var(--text-secondary)" }}>
                  {error instanceof Error ? error.message : "Fehler beim Laden der Daten"}
                </p>
              </div>
            )}

            {!isLoading && !isError && filtered.length === 0 && (
              <div
                className="flex items-center justify-center h-32 text-sm"
                style={{ color: "var(--text-muted)" }}
              >
                {t("retention.noResults")}
              </div>
            )}

            {!isLoading &&
              !isError &&
              filtered.map((emp) => (
                <div
                  key={emp.id}
                  className="row-hover grid items-center px-4 py-3 cursor-pointer"
                  style={{
                    gridTemplateColumns: "180px 1fr 105px 170px 90px 1fr 80px",
                    borderBottom: "1px solid var(--border)",
                  }}
                  onClick={() => setSelectedEmployeeId(emp.id)}
                >
                  {/* Name / ID */}
                  <div>
                    <div className="text-xs font-medium" style={{ color: "var(--text-primary)" }}>
                      {emp.displayName}
                    </div>
                    {emp.position && (
                      <div
                        className="text-[10px] mt-0.5 truncate"
                        style={{ color: "var(--text-muted)" }}
                      >
                        {emp.position}
                      </div>
                    )}
                  </div>

                  {/* Department + Bereich */}
                  <div>
                    <div className="text-sm" style={{ color: "var(--text-secondary)" }}>
                      {emp.department ?? "—"}
                    </div>
                    {emp.team && (
                      <div
                        className="text-[10px] mt-0.5"
                        style={{ color: "var(--accent)", opacity: 0.8 }}
                      >
                        {emp.team}
                      </div>
                    )}
                  </div>

                  {/* Tenure */}
                  <span className="font-mono-data text-xs" style={{ color: "var(--text-muted)" }}>
                    {emp.tenureMonths != null ? `${emp.tenureMonths}M` : "—"}
                  </span>

                  {/* Score bar */}
                  <div>
                    {emp.retentionScore != null && emp.riskLevel ? (
                      <ScoreBar score={emp.retentionScore} riskLevel={emp.riskLevel} />
                    ) : (
                      <span className="text-xs" style={{ color: "var(--text-muted)" }}>
                        —
                      </span>
                    )}
                  </div>

                  {/* Trend badge */}
                  <div>
                    <TrendBadge trendPp={emp.trendPp} />
                  </div>

                  {/* Top signals */}
                  <div className="flex flex-wrap gap-1 pr-2">
                    {emp.topSignals.slice(0, 3).map((s) => (
                      <span
                        key={s}
                        className="text-[10px] px-1.5 py-0.5 rounded"
                        style={{
                          background: "var(--bg-hover)",
                          color: "var(--text-muted)",
                          border: "1px solid var(--border)",
                        }}
                      >
                        {s}
                      </span>
                    ))}
                  </div>

                  {/* Risk badge */}
                  <div>{emp.riskLevel && <RiskBadge level={emp.riskLevel} />}</div>
                </div>
              ))}
          </div>

          {/* Footer */}
          {!isLoading && !isError && (
            <div
              className="px-4 py-2.5 flex items-center justify-between text-xs"
              style={{ borderTop: "1px solid var(--border)", color: "var(--text-muted)" }}
            >
              <span>
                {filtered.length} von {employees.length} Mitarbeitern
              </span>
              {summary && (
                <span className="font-mono-data">
                  Ø Score: {(summary.avgScore * 100).toFixed(0)}%
                </span>
              )}
            </div>
          )}
        </div>
      </div>
      {/* key = Personenwechsel setzt den lokalen Drawer-State zurück — siehe MeinTeam.tsx */}
      <EmployeeDetailDrawer
        key={selectedEmployeeId}
        employeeId={selectedEmployeeId}
        onClose={() => setSelectedEmployeeId(null)}
      />
    </AppLayout>
  );
}
